import { useContext, useEffect, useState } from "react";
import IOrder from "../../../models/IOrder";
import IWarehouse from "../../../models/IWarehouse";
import OwTitle from "../../shared/OwTitle/OwTitle";
import TitleTagEnum from "../../../models/TitleTagEnum";
import OwButton from "../../shared/OwButton/OwButton";
import UserContext from "../../../contexts/UserContext";
import VendorContext from "../../../contexts/VendorContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOrderEntryOnGetAccountOrderEntryItemsDTO from "../../../models/DTO/IOrderEntryOnGetAccountOrderEntryItemsDTO";
import IOrderEntryGetAccountOrderEntryItemsDTO from "../../../models/DTO/IOrderEntryGetAccountOrderEntryItemsDTO";
import ReportPeriodType from "../../../models/ReportPeriodTypeEnum";
import IOrder_Item from "../../../models/IOrder_Item";
import OrderEntryService from "../../../services/OrderEntryService";
import { RecentItemsRow } from "./RecentItemsRow/RecentItemsRow";
import "./AccountRecentItemsSidebar.scss";
import { IAddBulkItems } from "@models/IAddBulkItems";
import ICatalog_Item from "@models/ICatalog_Item";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import CatalogViewerCart from "../CatalogViewerCart/CatalogViewerCart";

type Props = {
    cancel: () => void;
    catalogGuid: string;
    addToOrder: (items: IAddBulkItems[]) => void;
    order: IOrder;
    warehouses: IWarehouse[];
};

export const AccountRecentItemsSidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [recentItems, setRecentItems] = useState<IOrder_Item[]>([]);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(20);
    const [itemsToAdd, setItemsToAdd] = useState<IAddBulkItems[]>([]);

    const getRecentAccountOrderEntryItems = () => {
        const payload: IOrderEntryGetAccountOrderEntryItemsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: props.order.Vendor?.guid || "",
            limit,
            offset,
            periodType: ReportPeriodType.LAST_SIX_MONTHS,
            catalogGuid: props.catalogGuid,
            priceGroupGuid: props.order.Price_Group?.guid || "",
        };

        socket.emit(EndpointsEnum.OrderEntryGetRecentAccountOrderEntryItems, payload);
    };

    const onGetRecentAccountOrderEntryItems = (data: IOrderEntryOnGetAccountOrderEntryItemsDTO) => {
        setRecentItems(data.items);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderEntryOnGetRecentAccountOrderEntryItems, fn: onGetRecentAccountOrderEntryItems },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getRecentAccountOrderEntryItems();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    let currency = props.order.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

    if (props.order.Currency) {
        currency = props.order.Currency;
    }

    const prefix = currency ? OrderEntryService.getCurrencySymbol(currency.name) : "$";

    const handleAddToOrder = (items: IAddBulkItems[]) => {
        const _itemsToAdd: IAddBulkItems[] = [];

        for (const itemToAdd of items) {
            let found = false;

            if (supplierModel.no_dupe_order_items) {
                for (const existingItem of props.order.Order_Items) {
                    if (Number(itemToAdd.item.catalog_item_id) === Number(existingItem.catalog_item_id)) {
                        found = true;
                    }
                }
            }

            if (!found) {
                _itemsToAdd.push(itemToAdd);
            } else {
                const notification: INotification = {
                    title: "item_already_in_cart",
                    message: itemToAdd.item.Catalog_Item?.name || "",
                    position: NotificationPositionEnum.bc,
                    level: NotificationLevelEnum.error,
                };

                uiContext.notifyFn(null, notification);
            }
        }

        if (_itemsToAdd.length) {
            const updatedItemsToAdd = [...itemsToAdd, ..._itemsToAdd];
            setItemsToAdd(updatedItemsToAdd);
        }
    };

    const removeCartItem = (item: ICatalog_Item, index: number) => {
        const newCartItems = [...itemsToAdd];
        newCartItems.splice(index, 1);
        setItemsToAdd(newCartItems);
    };

    const addCartToOrder = () => {
        props.addToOrder(itemsToAdd);
    };

    return (
        <div className="AccountRecentItemsSidebar container p-0 d-flex flex-column" style={{ height: "100%", overflow: "hidden" }}>
            {/* Fixed Header Section */}
            <div className={"d-flex flex-column p-2 flex-shrink-0"}>
                <div className={"d-flex justify-content-between align-items-center"}>
                    <OwTitle title="recent_items" tag={TitleTagEnum.H3} leftIconName={"fas fa-history"} />
                    <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={props.cancel} />
                </div>
            </div>

            {/* Scrollable Main Content Area */}
            <div className="flex-grow-1 p-2" style={{ overflowY: "auto", minHeight: 0 }}>
                {recentItems.map((item) => {
                    return (
                        <RecentItemsRow
                            key={item.guid}
                            item={item}
                            addToOrder={handleAddToOrder}
                            order={props.order}
                            prefix={prefix}
                            currency={currency}
                        />
                    );
                })}
            </div>

            {/* Fixed Cart Section */}
            <div className="flex-shrink-0">
                <CatalogViewerCart
                    order={props.order}
                    items={itemsToAdd}
                    removeCartItem={removeCartItem}
                    addCartToOrder={addCartToOrder}
                    supplierModel={supplierModel}
                    userModel={userModel}
                />
            </div>
        </div>
    );
};
